import { memo, useEffect, useState, useCallback } from 'react';
import { RoomAudioRenderer, useMaybeRoomContext } from '@livekit/components-react';
import { BlockLink } from 'BlockLink';
import { useTutorBotBottomSpacing } from 'TutorBotChat';
import { twMerge } from 'tailwind-merge';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { type AudioUiContext } from 'TutorBotConversation';
import { useErrorLogService } from 'ErrorLogging';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import { type Room } from 'livekit-client';
import { useStoreAudioTranscriptions } from '../hooks/useStoreAudioTranscriptions';
import { AudioVisualizer } from './AudioVisualizer';
import { AudioControlBar } from './AudioControlBar/AudioControlBar';
import { useDataReceivedEventListener } from '../hooks/useRoomEventListener';
import { algorithmMetadataEventListener, errorEventListener } from '../utils';
import { useSessionTimer } from '../hooks/useSessionTimer';
import { SessionWillEndSoonMessage } from './SessionWillEndSoonMessage/SessionWillEndSoonMessage';
import { ExitModal } from './ExitModal/ExitModal';
import { type SessionWillEndSoonMessageKey } from './SessionWillEndSoonMessage';
import '../locales/TutorBotVoiceAgent/TutorBotVoiceAgent-en.json';
import { type TutorBotDataReceivedCallback, type SupportedColors } from '../TutorBotVoiceAgent.types';

type Props = {
    uiContext: AudioUiContext;
    conversationId: string;
    color: SupportedColors | undefined;
    abortFromRoom: (() => void) | null;
    allowPostDiscussion: boolean;
    abortConfirmHeader?: string | null;
    abortConfirmText?: string | null;
    abortConfirmContinueButtonText?: string | null;
    abortConfirmCancelButtonText?: string | null;
    abortText: string;
    sessionWillEndSoonMessageKey?: SessionWillEndSoonMessageKey;
    onDataReceived?: TutorBotDataReceivedCallback;
};

type PresentationProps = Pick<
    Props,
    | 'abortFromRoom'
    | 'abortText'
    | 'allowPostDiscussion'
    | 'abortConfirmText'
    | 'abortConfirmContinueButtonText'
    | 'abortConfirmCancelButtonText'
> & {
    audioVisualizer: React.ReactNode;
    sessionTimer: ReturnType<typeof useSessionTimer>;
    audioControlBar: React.ReactNode;
    roomAudioRenderer: React.ReactNode;
    sessionWillEndSoonMessageKey?: SessionWillEndSoonMessageKey;
};

function goToPostDiscussion(room: Room | undefined) {
    room?.disconnect();
}

export function ConnectedLiveKitRoomUiPresentation({
    abortFromRoom,
    allowPostDiscussion,
    abortText,
    audioVisualizer,
    sessionTimer,
    audioControlBar,
    roomAudioRenderer,
    sessionWillEndSoonMessageKey,
}: PresentationProps) {
    const spacingBeneathLowestButton = useTutorBotBottomSpacing('h-[90px]');
    const room = useMaybeRoomContext();

    useEffect(() => {
        if (!room) return undefined;

        return () => {
            room.disconnect();
        };
    }, [room]);

    return (
        <>
            {/* This wrapper stretches to fill all the vertical space above the AudioControlBar */}
            <div className="relative flex-grow animate-fade-in [animation-duration:_.6s]">
                {/* The Audio Visualizer also stretches to fill that vertical space */}
                <div className="z-0 h-full">{audioVisualizer}</div>

                {/* The SessionWillEndSoonMessage is fixed to the bottom of the space above the AudioControlBar, and
                 z-indexed in front of the AudioVisualizer */}
                <div className="z-1 absolute bottom-7 w-full">
                    <SessionWillEndSoonMessage sessionTimer={sessionTimer} messageKey={sessionWillEndSoonMessageKey} />
                </div>
            </div>

            {/* The switchToTextMode link is fixed to the bottom of the screen with a height matching spacingBeneathLowestButton.
                The audioControlBar is fixed above that, so it has spacingBeneathLowestButton spacing beneath it. */}
            <div className="relative flex w-full animate-fade-in flex-col [animation-duration:_.6s]">
                {audioControlBar}
                <div className={twMerge('flex w-full items-center justify-center', spacingBeneathLowestButton)}>
                    {allowPostDiscussion && abortFromRoom && (
                        <BlockLink onPress={abortFromRoom} color="slate-grey">
                            {abortText}
                        </BlockLink>
                    )}
                </div>
            </div>
            {roomAudioRenderer}
            {/* <NoAgentNotification /> */}
            {/* We're no longer showing the LiveTranscript in the UI, but you can uncomment this if it's useful for debugging */}
            {/* <LiveTranscript token={connectionDetails?.participantToken} /> */}
        </>
    );
}

function useRequestAlgorithmMetadata({ room }: { room: Room | undefined }) {
    // Our python agent waits for the participant to connect, then proactively publishes the algorithm metadata.
    // But in case the client wasn't listening yet, we also request it here after we detect that the server has
    // entered the room.
    useEffect(() => {
        if (room?.state === 'connected' && Array.from(room?.remoteParticipants.values()).length > 0) {
            room!.localParticipant.publishData(
                new TextEncoder().encode(JSON.stringify({ type: 'requestAlgorithmMetadata' })),
                {
                    reliable: true,
                },
            );
        }
    }, [room, room?.state, room?.remoteParticipants]);
}

function useHandledDataReceived({
    room,
    onDataReceived,
    conversationId,
    allowPostDiscussion,
}: {
    room: Room | undefined;
    onDataReceived: TutorBotDataReceivedCallback | undefined;
    conversationId: Props['conversationId'];
    allowPostDiscussion: Props['allowPostDiscussion'];
}) {
    const handleDataReceived = useCallback<TutorBotDataReceivedCallback>(
        params => {
            if (params.message?.type === 'closeWindow' && allowPostDiscussion) {
                goToPostDiscussion(room);
                return;
            }
            onDataReceived?.(params);
        },
        [room, onDataReceived, allowPostDiscussion],
    );

    useDataReceivedEventListener({
        conversationId,
        listeners: [algorithmMetadataEventListener, errorEventListener, handleDataReceived],
    });
}

function ShowExitModalIfNecessary({
    setUserPressedCloseButton,
    abortConfirmText,
    abortConfirmCancelButtonText,
    abortConfirmHeader,
    abortFromRoom,
    abortConfirmContinueButtonText,
    allowPostDiscussion,
    userPressedCloseButton,
}: {
    setUserPressedCloseButton: (value: boolean) => void;
    abortConfirmText: Props['abortConfirmText'];
    abortConfirmCancelButtonText: Props['abortConfirmCancelButtonText'];
    abortConfirmHeader: Props['abortConfirmHeader'];
    abortConfirmContinueButtonText: Props['abortConfirmContinueButtonText'];
    allowPostDiscussion: Props['allowPostDiscussion'];
    userPressedCloseButton: boolean;
    abortFromRoom: Props['abortFromRoom'];
}) {
    const handleCancel = useCallback(() => setUserPressedCloseButton(false), [setUserPressedCloseButton]);
    const handleConfirm = useCallback(() => {
        setUserPressedCloseButton(false);
        abortFromRoom?.(); // abortFromRoom will always exist when we get here.
    }, [abortFromRoom, setUserPressedCloseButton]);
    const { isMobile } = useIsMobileMixin();

    const showExitModal =
        userPressedCloseButton &&
        !!abortConfirmHeader &&
        !!abortConfirmText &&
        !!abortConfirmContinueButtonText &&
        !!abortConfirmCancelButtonText &&
        !!abortFromRoom;

    useWarnOnAbortPropMissing({
        allowPostDiscussion,
        userPressedCloseButton,
        abortConfirmHeader,
        abortConfirmText,
        abortConfirmContinueButtonText,
        abortConfirmCancelButtonText,
        abortFromRoom,
    });

    return (
        <>
            {showExitModal && (
                <ExitModal
                    text={abortConfirmText}
                    cancelLabel={abortConfirmCancelButtonText}
                    title={abortConfirmHeader}
                    confirmLabel={abortConfirmContinueButtonText}
                    onCancel={handleCancel}
                    onConfirm={handleConfirm}
                    isMobile={isMobile}
                />
            )}
        </>
    );
}

// In order to get access to the room context, we split out the UI inside the LiveKitRoom
// into a separate component.
function ConnectedLiveKitRoomUiComponent({
    conversationId,
    uiContext,
    abortText,
    allowPostDiscussion,
    abortConfirmHeader,
    abortConfirmText,
    abortConfirmContinueButtonText,
    abortConfirmCancelButtonText,
    abortFromRoom,
    color,
    sessionWillEndSoonMessageKey,
    onDataReceived,
}: Props) {
    useStoreAudioTranscriptions({ conversationId, uiContext });

    const room = useMaybeRoomContext();
    useRequestAlgorithmMetadata({ room });
    useHandledDataReceived({ room, onDataReceived, conversationId, allowPostDiscussion });

    const sessionTimer = useSessionTimer({ uiContext });
    const [userPressedCloseButton, setUserPressedCloseButton] = useState(false);

    const handleEndPress = useCallback(() => {
        setUserPressedCloseButton(true);
    }, [setUserPressedCloseButton]);

    // If, after the user has clicked close and opened the confirm dialog, things
    // change and they're allowed to view PostDiscussion then send them to PostDiscussion. It's
    // a bit jarring, but it's an edge case (hopefully) (This happens when a coach session gets
    // marked as complete while the confirm is open)
    useEffect(() => {
        if (allowPostDiscussion && userPressedCloseButton) goToPostDiscussion(room);
    }, [allowPostDiscussion, userPressedCloseButton, room]);

    if (room?.state !== 'connected') return <FrontRoyalSpinner className="centered" />;

    return (
        <>
            <ConnectedLiveKitRoomUiPresentation
                audioVisualizer={<AudioVisualizer color={color} />}
                sessionTimer={sessionTimer}
                abortFromRoom={abortFromRoom}
                allowPostDiscussion={allowPostDiscussion}
                abortText={abortText}
                audioControlBar={<AudioControlBar onEndPress={handleEndPress} />}
                roomAudioRenderer={<RoomAudioRenderer />}
                sessionWillEndSoonMessageKey={sessionWillEndSoonMessageKey}
            />
            <ShowExitModalIfNecessary
                userPressedCloseButton={userPressedCloseButton}
                abortFromRoom={abortFromRoom}
                setUserPressedCloseButton={setUserPressedCloseButton}
                abortConfirmText={abortConfirmText}
                abortConfirmCancelButtonText={abortConfirmCancelButtonText}
                abortConfirmHeader={abortConfirmHeader}
                abortConfirmContinueButtonText={abortConfirmContinueButtonText}
                allowPostDiscussion={allowPostDiscussion}
            />
        </>
    );
}

function useWarnOnAbortPropMissing({
    allowPostDiscussion,
    userPressedCloseButton,
    abortConfirmHeader,
    abortConfirmText,
    abortConfirmContinueButtonText,
    abortConfirmCancelButtonText,
    abortFromRoom,
}: {
    allowPostDiscussion: boolean;
    userPressedCloseButton: boolean;
    abortConfirmHeader: Props['abortConfirmHeader'];
    abortConfirmText: Props['abortConfirmText'];
    abortConfirmContinueButtonText: Props['abortConfirmContinueButtonText'];
    abortConfirmCancelButtonText: Props['abortConfirmCancelButtonText'];
    abortFromRoom: Props['abortFromRoom'];
}) {
    const ErrorLogService = useErrorLogService();
    useEffect(() => {
        // See comment above that starts with "If, after the user has clicked"
        if (allowPostDiscussion) return;
        if (!userPressedCloseButton) return;
        if (!abortFromRoom) {
            ErrorLogService.notifyInProd('Cannot show exit modal because abortFromRoom is null');
            return;
        }
        if (!abortConfirmHeader) {
            ErrorLogService.notifyInProd('Cannot show exit modal because abortConfirmHeader is null');
            abortFromRoom();
        }
        if (!abortConfirmText) {
            ErrorLogService.notifyInProd('Cannot show exit modal because abortConfirmText is null');
            abortFromRoom();
        }
        if (!abortConfirmContinueButtonText) {
            ErrorLogService.notifyInProd('Cannot show exit modal because abortConfirmContinueButtonText is null');
            abortFromRoom();
        }
        if (!abortConfirmCancelButtonText) {
            ErrorLogService.notifyInProd('Cannot show exit modal because abortConfirmCancelButtonText is null');
            abortFromRoom();
        }
    }, [
        allowPostDiscussion,
        userPressedCloseButton,
        abortConfirmHeader,
        abortConfirmText,
        abortConfirmContinueButtonText,
        abortConfirmCancelButtonText,
        ErrorLogService,
        abortFromRoom,
    ]);
}

export const ConnectedLiveKitRoomUi = memo(ConnectedLiveKitRoomUiComponent) as typeof ConnectedLiveKitRoomUiComponent;
